import { toPersianDigits } from '@bookora/shared';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  WHY A BRANCH ACTION IS BLOCKED — «[why]. [what to do].»
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Every blocked branch action says why, in the kit's `disabledReason`
 * (focusable, its reason the accessible description, a bubble on focus). The
 * panel predicts the refusals the server would make — archive the primary, a
 * branch with future appointments or stock, a full plan — so the button says
 * it BEFORE the click instead of ending on a red toast.
 *
 * `test/reason-copy.test.ts` reads every exported const here and holds it to
 * the two-clause shape; functions are covered where they compose. Shared with
 * `rooms-panel.tsx` / `working-hours-panel.tsx` and the staff and inventory
 * screens, so one sentence never exists twice.
 */

/* ── Archive (three disabledReasons, one per server refusal) ─────────────── */

export const BRANCH_PRIMARY_ARCHIVE =
  'شعبهٔ اصلی رو نمی‌شه بایگانی کرد. اول شعبهٔ دیگری رو اصلی کنید.';

export function branchFutureAppointmentsReason(count: number | null): string {
  return count && count > 0
    ? `این شعبه ${toPersianDigits(count)} نوبت آینده داره. اول نوبت‌ها رو منتقل یا لغو کنید.`
    : BRANCH_FUTURE_APPOINTMENTS;
}

export const BRANCH_FUTURE_APPOINTMENTS =
  'این شعبه نوبت آینده داره. اول نوبت‌ها رو منتقل یا لغو کنید.';

export const BRANCH_HAS_STOCK =
  'در انبار این شعبه موجودی مونده. اول کالاها رو به شعبهٔ دیگری منتقل کنید.';

/* ── Add / restore ───────────────────────────────────────────────────────── */

/**
 * What someone without `settings.billing` is told to do about the plan — the
 * panel's one sentence for it (the plan-lock screens say the same): the plan
 * is the OWNER's, so it is the owner who is told, not «مدیر».
 */
const PLAN_OWNER_NEXT = 'برای ارتقای پلن به مالک کلینیک خبر بدید.';

/**
 * The plan's branch limit is reached («{used} از {max}»). Billing holders are
 * told to upgrade (and get «مقایسهٔ پلن‌ها» beside it); everyone else whom to
 * tell — a dead «ارتقا بدید» for someone who cannot is not an instruction.
 */
export function branchLimitReason(planName: string | null, canManageBilling: boolean): string {
  const plan = planName ? `پلن ${planName}` : 'پلن فعلی';
  return canManageBilling
    ? `سقف شعبه‌های ${plan} پر است. برای شعبهٔ بیشتر، پلن رو ارتقا بدید.`
    : `سقف شعبه‌های ${plan} پر است. ${PLAN_OWNER_NEXT}`;
}

/**
 * «بازگردانی» on a plan without the `branches` feature (a downgraded clinic):
 * a restored branch would be a second live site, which the server refuses
 * with 402 — said on the button instead. The plan is named from data.
 */
export function branchRestoreLockedReason(
  planName: string | null,
  canManageBilling: boolean,
): string {
  const plan = planName ? `پلن ${planName}` : 'پلن بالاتر';
  return canManageBilling
    ? `بازگردانی شعبه در ${plan} است. برای بازگردانی، پلن رو ارتقا بدید.`
    : `بازگردانی شعبه در ${plan} است. ${PLAN_OWNER_NEXT}`;
}

export const BRANCH_ARCHIVED_MAKE_PRIMARY = 'این شعبه بایگانی شده. اول اون رو بازگردانی کنید.';

/* ── The form ────────────────────────────────────────────────────────────── */

export const BRANCH_NAME_EMPTY = 'نام شعبه رو وارد کنید؛ مثلاً «شعبهٔ سعادت‌آباد».';

export const BRANCH_SLUG_INVALID =
  'نشانی صفحهٔ شعبه فقط حروف کوچک لاتین، رقم و خط تیره می‌پذیره. نشانی رو اصلاح کنید.';

export const BRANCH_SLUG_TAKEN = 'این نشانی برای شعبهٔ دیگری ثبت شده. نشانی دیگری بنویسید.';

export const BRANCH_FORM_INVALID = 'چند فیلد نیاز به اصلاح داره. پیام زیر هر فیلد رو بررسی کنید.';

export const BRANCH_SLUG_CHECKING = 'آزاد بودن نشانی هنوز بررسی می‌شه. چند لحظه صبر کنید.';

/* ── Staff and the caller ────────────────────────────────────────────────── */

export const STAFF_BRANCH_REQUIRED = 'هر همکار حداقل در یک شعبه کار می‌کنه. یک شعبه انتخاب کنید.';

/** A branch-restricted caller with no branch at all. */
export const NO_BRANCH_ASSIGNED =
  'برای حساب شما شعبه‌ای مشخص نشده. از مدیر کلینیک بخواید شعبهٔ شما رو تعیین کنه.';

/** A form that must name its branch (a multi-branch clinic) and does not yet. */
export const BRANCH_NOT_CHOSEN = 'شعبه انتخاب نشده. شعبهٔ این مورد رو انتخاب کنید.';
